import {useQuery} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {JobDefinition, JobExecutionListItem, TaskDefinition} from '@/types/background-jobs'
import type {Page} from '@/types/page'

export interface UseJobResult {
    job: JobDefinition | undefined
    tasks: TaskDefinition[]
    executions: JobExecutionListItem[]
    isJobLoading: boolean
    jobError: Error | null
}

/**
 * Composite read hook for the JobDetail page — fetches the job definition,
 * its task definitions, and the most recent executions in three independent
 * queries (so failure of one does not knock out the others). Returns a
 * stable shape regardless of in-flight state.
 */
export function useJob(id: string | undefined): UseJobResult {
    const services = useServices()

    const enabled = !!id

    const jobQuery = useQuery({
        queryKey: ['job', id],
        queryFn: () => services.jobs.detail(id!),
        enabled,
    })

    const tasksQuery = useQuery({
        queryKey: ['job-tasks', id],
        queryFn: () => services.jobs.tasks(id!),
        enabled,
    })

    const executionsQuery = useQuery({
        queryKey: ['job-executions', id],
        queryFn: () => services.jobs.jobExecutions(id!, {size: 10, sort: 'startedAt,desc'}),
        enabled,
    })

    const executions = (executionsQuery.data as Page<JobExecutionListItem> | undefined)?.content ?? []

    return {
        job: jobQuery.data,
        tasks: tasksQuery.data ?? [],
        executions,
        isJobLoading: jobQuery.isLoading,
        jobError: (jobQuery.error as Error | null) ?? null,
    }
}
